<template>
  <div>
    <!-- Header -->
    <div v-if="user" class="d-flex align-center">
      <base-avatar :name="user.fullName" :photo="user.photo" size="50" />
      <div class="ml-5">
        <p class="text-h4">
          {{ user.fullName }}
        </p>
        <p class="lightCarbon--text font-weight-regular caption">
          {{ user.email }}
          <span v-if="user.email" class="mx-2">
            •
          </span>
          {{ user.batch }}
        </p>
      </div>
    </div>

    <v-sheet v-if="serverFailure" class="pa-5 d-flex flex-column align-center">
      <p class="text-h5 font-weight-medium">
        There was some issue in getting the submission.
      </p>
      <p>
        Please try refreshing the page.
      </p>
    </v-sheet>

    <v-row v-else class="mt-2 container-classes">
      <v-col cols="12" md="6" xl="7">
        <submission-details
          :assignment="assignment"
          :loading="loading"
          :submission="submission"
        />
      </v-col>
      <v-col v-if="assignment.uuid" cols="12" md="6" xl="5">
        <re-evaluate-submission
          v-if="iqaReview.uuid"
          :iqa-review="iqaReview"
          :review="review"
          :submission-id="submission.uuid"
          :submission-list="allSubmissionsList"
          @change-submission="getSubmissionById($event)"
          @submit="handleReviewSubmit"
        />
        <evaluate-submission
          v-else
          :assignment-id="assignment.uuid"
          :iqa-review-already-exists="iqaReviewAlreadyExists"
          :previous-review-id="previousReviewId"
          :review="review"
          :submission-id="submission.uuid"
          :submission-list="allSubmissionsList"
          @change-submission="getSubmissionById($event)"
          @submit="handleReviewSubmit"
        />
      </v-col>
    </v-row>
  </div>
</template>

<script>
import SubmissionDetails from '@/components/assignments/SubmissionDetails'
import EvaluateSubmission from './EvaluateSubmission'
import ReEvaluateSubmission from './ReEvaluateSubmission'

import { REVIEW_EVALUATION_STATUS_CHOICES } from '@/utils/constants'

export default {
  name: 'ViewSubmission',

  components: { SubmissionDetails, EvaluateSubmission, ReEvaluateSubmission },

  data: () => ({
    // UUID of the currently selected submission
    selectedSubmissionId: '',

    // Assignment meta-data
    assignment: {
      title: 'Assignment',
      course: 'Course',
    },

    // Student details
    user: null,

    // Stores currently selected submission (determined from selectedSubmissionId)
    submission: {},

    // List of all submissions of the student (ordered by date i.e latest submission at index 0)
    allSubmissionsList: [],

    /**
     * Stores review (if already exists) of the submission
     * Submission is considered reviewed only if a review object exists with any status other than 'not_reviewed' or 'under_review'
     * All possible values of review.status are:
     *    not_reviewed
     *    under_review
     *    accepted
     *    rejected
     */
    review: {
      uuid: '',
      comments: '',
      marks: 0,
      status: null,
    },

    iqaReview: {
      uuid: '',
      comments: '',
      marks: 0,
      status: null,
    },

    // Tracks submission loading
    loading: false,

    serverFailure: false,
  }),

  computed: {
    previousReviewId() {
      if (this.allSubmissionsList.length > 1)
        return this.allSubmissionsList[1].review_uuid
      return null
    },

    iqaReviewAlreadyExists() {
      let reviewExists = false
      const { uuid, status } = this.iqaReview
      if (!uuid) {
        reviewExists = false
      } else if (
        status === REVIEW_EVALUATION_STATUS_CHOICES.NOT_STARTED ||
        status === REVIEW_EVALUATION_STATUS_CHOICES.UNDER_EVALUATION
      ) {
        reviewExists = false
      } else {
        reviewExists = true
      }
      return reviewExists
    },
  },

  mounted() {
    this.getSubmissionById(this.$route.params.submissionId, true)
  },

  methods: {
    async getSubmissionById(
      submissionId,
      fetchPreviousSubmissionsList = false
    ) {
      this.loading = true
      try {
        const res = await this.$api.assignments.getSubmissionById(submissionId)
        const data = res.data.data
        if (fetchPreviousSubmissionsList) {
          this.getPreviousSubmissionsList(data.assignment.uuid, data.user.uuid)
        }
        this.setSubmissionDataInState(data)
      } catch (error) {
        console.error(error)
      } finally {
        this.loading = false
      }
    },

    setSubmissionDataInState(data) {
      this.assignment = data.assignment

      this.submission = {
        uuid: data.uuid,
        text: data.submission,
        attachments: data.attachments,
        status: data.status,
        references: data.references,
      }

      this.user = {
        fullName: data.user.full_name,
        email: data.user.email,
        batch: data.batch,
      }

      this.selectedSubmissionId = data.uuid

      this.review = {
        uuid: data.review.uuid,
        comments: data.review.comments,
        marks: data.review.marks.toFixed(2),
        status: data.review.status,
        evaluationStatus: data.review.evaluation_status,
        challengerComments: data.challenger_comments,
        professor: data.review.professor,
        date_published: data.review.date_published,
      }

      if (data.iqa_review) {
        this.iqaReview = {
          uuid: data.iqa_review.uuid,
          comments: data.iqa_review.comments,
          evaluatorNotes: data.evaluator_notes,
          marks: data.iqa_review.marks.toFixed(2),
          status: data.iqa_review.status,
        }
      } else {
        this.iqaReview = {
          uuid: '',
          comments: '',
          marks: 0,
          status: null,
        }
      }
    },

    handleReviewSubmit(data) {
      switch (data.evaluationStatus) {
        case REVIEW_EVALUATION_STATUS_CHOICES.CHANGES_REQUESTED:
          this.review.evaluationStatus = data.evaluationStatus
          this.iqaReview = data.iqaReview
          break

        case REVIEW_EVALUATION_STATUS_CHOICES.ACCEPTED:
          this.review.evaluationStatus = data.evaluationStatus
          break

        case REVIEW_EVALUATION_STATUS_CHOICES.REVIEW_CHANGED:
          this.review.evaluationStatus = data.evaluationStatus
          break

        default:
          break
      }
    },

    async getPreviousSubmissionsList(assignmentId, studentId) {
      try {
        const res = await this.$api.assignments.getPreviousSubmissionsList({
          assignmentId,
          studentId,
        })
        this.allSubmissionsList = res.data.data
      } catch (error) {
        console.error(error)
      }
    },
  },
}
</script>

<style scoped lang="scss">
.container-classes::v-deep {
  .assignment-container-scrollbars {
    &::-webkit-scrollbar {
      width: 8px; /* width of the entire scrollbar */
      height: 8px;
    }

    &::-webkit-scrollbar-track {
      background: #00000017; /* color of the tracking area */
    }

    &::-webkit-scrollbar-thumb {
      background-color: #8d96b2; /* color of the scroll thumb */
      border-radius: 20px; /* roundness of the scroll thumb */
      border: 3px solid #00000017; /* creates padding around scroll thumb */
    }
  }

  .container-fixed-height {
    @media #{map-get($display-breakpoints, 'md-and-up')} {
      height: calc(100vh - 240px);
    }
  }
}
</style>
